import { ArrowLeft } from '@phosphor-icons/react';
import { cn, Container, Inline, Stack } from '@reelplicate/ui';
import { type ReactNode, useId } from 'react';
import { Link as RouterLink } from 'react-router';

/**
 * A settings sub-page (Figma "Settings — General" and siblings, 320:2…): a
 * "Settings" back link, the page header (Heading/H1, one line of Body/Medium
 * and the page actions), then its sections 32px apart. Content is 880 wide
 * (Billing is 960), centred in the main area.
 */
export function SettingsPageLayout({
  title,
  description,
  actions,
  wide = false,
  showBack = true,
  back = { to: '/settings', label: 'Settings' },
  leading,
  onSubmit,
  children,
}: {
  title: string;
  description: ReactNode;
  actions?: ReactNode;
  wide?: boolean;
  /** The "Settings" back link; off for pages that reuse this layout outside Settings. */
  showBack?: boolean;
  /** Where the back link goes, for pages outside Settings. */
  back?: { to: string; label: string };
  /** Before the title, e.g. an avatar. */
  leading?: ReactNode;
  /** Where the page's Save button submits to. */
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
}) {
  return (
    <Container className="pt-10 pb-16">
      <Stack
        as="form"
        gap={8}
        onSubmit={onSubmit ?? ((event) => event.preventDefault())}
        noValidate
        className={cn('mx-auto w-full', wide ? 'max-w-240' : 'max-w-220')}
      >
        {showBack && (
          <RouterLink
            to={back.to}
            className="group/back text-label-md text-text-secondary hover:text-text-primary focus-visible:ring-border-focus inline-flex items-center gap-1.5 self-start rounded-xs outline-none focus-visible:ring-2"
          >
            <ArrowLeft aria-hidden className="size-4" />
            {/* Figma Link (Neutral): a 1.25px underline on hover. */}
            <span className="decoration-[1.25px] underline-offset-[3px] group-hover/back:underline">
              {back.label}
            </span>
          </RouterLink>
        )}
        <Inline as="header" gap={3} wrap>
          {leading}
          <Stack gap={1} className="min-w-0 flex-1">
            <h1 className="text-heading-h1 text-text-primary">{title}</h1>
            <p className="text-body-md text-text-secondary">{description}</p>
          </Stack>
          {actions && (
            <Inline gap={3} className="shrink-0">
              {actions}
            </Inline>
          )}
        </Inline>
        {children}
      </Stack>
    </Container>
  );
}

/**
 * A titled group on a settings page: Heading/H4 title (danger for the
 * danger zone), an optional Body/Small line, an optional action on the
 * right, then its card 12px below.
 */
export function SettingsSection({
  title,
  description,
  action,
  danger = false,
  children,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  danger?: boolean;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <Stack as="section" gap={3} aria-labelledby={id}>
      <Inline gap={3} alignY="bottom">
        <Stack gap={0.5} className="min-w-0 flex-1">
          <h2
            id={id}
            className={cn(
              'text-heading-h4',
              danger ? 'text-status-danger-fg' : 'text-text-primary',
            )}
          >
            {title}
          </h2>
          {description && (
            <p className="text-body-sm text-text-secondary">{description}</p>
          )}
        </Stack>
        {action}
      </Inline>
      {children}
    </Stack>
  );
}

/** The white card a section's rows sit in: 20px side padding, rows divided. */
export function SettingsCard({
  danger = false,
  className,
  children,
}: {
  danger?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Stack
      gap={0}
      className={cn(
        'bg-bg-surface rounded-lg border px-5',
        '[&>*]:border-border-subtle [&>*:not(:last-child)]:border-b',
        danger ? 'border-status-danger-border' : 'border-border-subtle',
        className,
      )}
    >
      {children}
    </Stack>
  );
}

/**
 * Figma "Settings Row" (101:13424): label (+ optional badge) and a
 * one-sentence description on the left, the control on the right, at least
 * 64 tall. Disabled rows grey out and say why in the description.
 */
export function SettingsRow({
  label,
  badge,
  description,
  control,
  disabled = false,
  labelId,
}: {
  label: ReactNode;
  badge?: ReactNode;
  description?: ReactNode;
  control?: ReactNode;
  disabled?: boolean;
  /** Id on the label, to name the control with `aria-labelledby`. */
  labelId?: string;
}) {
  return (
    <Inline gap={6} className="min-h-16 py-4">
      <Stack gap={0.5} className="min-w-0 flex-1">
        <Inline gap={2}>
          <p
            id={labelId}
            className={cn(
              'text-body-md-strong',
              disabled ? 'text-text-disabled' : 'text-text-primary',
            )}
          >
            {label}
          </p>
          {badge}
        </Inline>
        {description && (
          <p
            className={cn(
              'text-body-sm',
              disabled ? 'text-text-disabled' : 'text-text-secondary',
            )}
          >
            {description}
          </p>
        )}
      </Stack>
      {control && (
        <Inline gap={2} className="shrink-0">
          {control}
        </Inline>
      )}
    </Inline>
  );
}

/**
 * A row for one thing in a list (an account, agent, key, session, invoice):
 * an optional 40px tile, title (+ badge) over a Caption line, then whatever
 * sits on the right.
 */
export function ListRow({
  tile,
  title,
  titleBadge,
  meta,
  children,
}: {
  tile?: ReactNode;
  title: ReactNode;
  titleBadge?: ReactNode;
  meta: ReactNode;
  children?: ReactNode;
}) {
  return (
    // Figma: 14px apart, 14px top and bottom.
    <Inline className="gap-3.5 py-3.5">
      {tile && (
        <span className="bg-bg-subtle flex size-10 shrink-0 items-center justify-center rounded-md">
          {tile}
        </span>
      )}
      <Stack gap={0.5} className="min-w-0 flex-1">
        <Inline gap={2}>
          <p className="text-body-md-strong text-text-primary truncate">{title}</p>
          {titleBadge}
        </Inline>
        <p className="text-caption text-text-secondary truncate">{meta}</p>
      </Stack>
      {children}
    </Inline>
  );
}
